import { alpha, useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { Layout, PlotData, Shape } from "plotly.js";
import { useMemo } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { defaultLayout, setSelectedDateFromPlotClick } from "../plotting";
import { GroupedScrapChefUsageSeries } from "../types";

interface ScrapChefUsagePlotProps {
  scrapChefUsageSeries: GroupedScrapChefUsageSeries;
  setSelectedDate: (date: Date) => void;
}

export const ScrapChefUsagePlot = ({
  scrapChefUsageSeries,
  setSelectedDate,
}: ScrapChefUsagePlotProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const usageBlue = theme.palette.data.blue;
  const usageOrange = theme.palette.data.orange;
  const referenceLineColor = theme.palette.text.secondary;

  const layout = useMemo(
    () => buildLayout(scrapChefUsageSeries, t, referenceLineColor),
    [scrapChefUsageSeries, t, referenceLineColor]
  );
  const data = useMemo(
    () => buildFigData(scrapChefUsageSeries, t, usageBlue, usageOrange),
    [scrapChefUsageSeries, t, usageBlue, usageOrange]
  );

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={data}
        layout={layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
    </div>
  );
};

const buildFigData = (
  scrapChefUsageSeries: GroupedScrapChefUsageSeries,
  t: (key: string) => string,
  usageBlue: string,
  usageOrange: string
): Partial<PlotData>[] => {
  const { values } = scrapChefUsageSeries;

  if (!values.date.length) {
    return [];
  }

  const data: Partial<PlotData>[] = [
    {
      x: values.date,
      y: values.rollingPercentage,
      type: "scatter",
      mode: "lines",
      name: t("scrapChefUsage"),
      line: {
        color: usageBlue,
        width: 3,
      },
      fill: "tonexty",
      fillcolor: alpha(usageBlue, 0.1),
    },
  ];

  const hasEditedOutsideData =
    values.scrapchefRecipeEditedOutsidePercentage.some((val) => val !== null);

  if (hasEditedOutsideData) {
    data.push({
      x: values.date,
      y: values.scrapchefRecipeEditedOutsidePercentage,
      type: "scatter",
      mode: "lines",
      name: t("scrapChefUsageEditedOutside"),
      line: {
        color: usageOrange,
        width: 3,
      },
      fill: "tonexty",
      fillcolor: alpha(usageOrange, 0.1),
    });
  }

  return data;
};

const buildLayout = (
  scrapChefUsageSeries: GroupedScrapChefUsageSeries,
  t: (key: string) => string,
  referenceLineColor: string
): Partial<Layout> => {
  const shapes: Partial<Shape>[] = [];

  if (scrapChefUsageSeries.developmentStartDate) {
    shapes.push({
      type: "line" as const,
      x0: scrapChefUsageSeries.developmentStartDate,
      x1: scrapChefUsageSeries.developmentStartDate,
      y0: 0,
      y1: 1,
      yref: "paper" as const,
      line: {
        color: referenceLineColor,
        dash: "dash" as const,
        width: 2,
      },
    });
  }

  if (scrapChefUsageSeries.highUsageStartDate) {
    shapes.push({
      type: "line" as const,
      x0: scrapChefUsageSeries.highUsageStartDate,
      x1: scrapChefUsageSeries.highUsageStartDate,
      y0: 0,
      y1: 1,
      yref: "paper" as const,
      line: {
        color: referenceLineColor,
        dash: "dash" as const,
        width: 2,
      },
    });
  }

  const hasEditedOutsideData =
    scrapChefUsageSeries.values.scrapchefRecipeEditedOutsidePercentage.some(
      (val) => val !== null
    );

  return {
    ...defaultLayout,
    yaxis: {
      ...defaultLayout.yaxis,
      title: { text: t("scrapChefUsage") + " (%)" },
      range: [-2, 102],
      tickformat: ".1f",
      ticksuffix: "%",
    },
    xaxis: {
      ...defaultLayout.xaxis,
      title: { text: t("date") },
    },
    hovermode: "x unified",
    showlegend: hasEditedOutsideData,
    shapes: shapes,
  };
};
